import { DeeplyReadonly, Filter, Query } from '@cubejs-client/core';
import { Grid, Stack, TextField } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDetailDomainType, I18nNamespace } from '~/common/types';
import { DimPurchaseOrderField } from '~/common/types/cubes/DimPuchaseOrder';
import { DimPurchaseOrderItemField } from '~/common/types/cubes/DimPurchaseOrderItem';
import { usePaginatedCube } from '~/components/CubeTable/usePaginatedCube';
import {
  ColumnSelector,
  DataGridProStyled,
  DataGridWidgetHeader,
  DensityButton,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';

import { usePurchaseOrderItemListGridConfig } from './PurchaseOrderItemListWidget.dataGrid';

const domain = BaseDetailDomainType.PURCHASE_ORDER_ITEM_DETAILS;

interface PurchaseOrderItemListWidgetProps {
  filters: Filter[];
}

const addSearchFilter = (filters: Filter[], search: string): Filter[] => {
  if (!search) {
    return filters;
  }
  return [
    ...filters,
    {
      member: DimPurchaseOrderItemField.TITLE,
      operator: 'contains',
      values: [search],
    },
  ];
};

export const PurchaseOrderItemListWidget = ({ filters }: PurchaseOrderItemListWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.base, { keyPrefix: 'PurchaseOrderItemListWidget' });

  const [search, setSearch] = useState('');

  const query: DeeplyReadonly<Query> = {
    measures: [],
    dimensions: [
      DimPurchaseOrderField.PRIMARY_SOURCE_REFERENCE,
      DimPurchaseOrderField.SUPPLIER_ID,
      DimPurchaseOrderItemField.ID,
      DimPurchaseOrderItemField.STATUS,
      DimPurchaseOrderItemField.LINE_ITEM_NUMBER,
      DimPurchaseOrderItemField.TITLE,
      DimPurchaseOrderItemField.PRICE_PER_UNIT,
      DimPurchaseOrderItemField.QUANTITY,
      DimPurchaseOrderItemField.VALUE,
      DimPurchaseOrderItemField.CURRENCY,
      DimPurchaseOrderItemField.EXCHANGE_RATE,
      DimPurchaseOrderItemField.IS_INVOICE_FINAL,
      DimPurchaseOrderItemField.IS_DELIVERY_FINAL,
      DimPurchaseOrderItemField.IS_DELIVERY_RECEIVED,
      DimPurchaseOrderItemField.IS_INVOICE_RECEIVED,
      DimPurchaseOrderItemField.IS_INVOICE_EXAMINATION_REQUIRED,
      DimPurchaseOrderItemField.IS_RETURN_ITEM,
      DimPurchaseOrderItemField.PURCHASE_ORDER_ID,
      DimPurchaseOrderItemField.PURCHASE_REQUISITION_ITEM_ID,
      DimPurchaseOrderItemField.COMMODITY_GROUP_ID,
      DimPurchaseOrderItemField.COMMODITY_ID,
      DimPurchaseOrderItemField.CONTRACT_ITEM_ID,
      DimPurchaseOrderItemField.ORGANIZATION_ID,
      DimPurchaseOrderItemField.UNIT_ID,
      DimPurchaseOrderItemField.SUBMISSION_PERIOD_EXPIRES_AT,
      DimPurchaseOrderItemField.CREATED_AT,
      DimPurchaseOrderItemField.UPDATED_AT,
      DimPurchaseOrderItemField.DESCRIPTION,
    ],
    order: {
      [DimPurchaseOrderItemField.VALUE]: 'desc',
    },
    filters: addSearchFilter(filters, search),
  };

  const { rows, rowCount, loading, paginationModel, onPaginationModelChange } = usePaginatedCube(query, {
    countMeasure: DimPurchaseOrderItemField.COUNT,
  });

  const apiRef = useGridApiRef();
  const config = usePurchaseOrderItemListGridConfig();

  const {
    gridProps,
    onDensityChange,
    onRemoteStateReset,
    loading: loadingRemoteState,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });
  const skip = loadingRemoteState;

  return (
    <Widget hover="js">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<DomainIcon domain={domain} />} />}
        actionsRight={
          <Stack direction="row">
            <TextField
              value={search}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSearch(event.target.value);
              }}
              sx={{ minWidth: 300 }}
              label={t('search')}
              variant="outlined"
            />
          </Stack>
        }
      />
      <WidgetContent>
        <Grid
          container
          direction="row"
          spacing={4}
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            alignContent: 'flex-start',
          }}
        >
          <Grid size={12}>
            <DataGridWidgetHeader
              disablePadding
              left={t('positions')}
              right={
                <Stack direction="row">
                  <DensityButton density={gridProps.density} onDensityChange={onDensityChange} />
                  <ColumnSelector
                    columns={gridProps.columns}
                    columnVisibilityModel={gridProps.columnVisibilityModel}
                    onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
                  />
                  <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
                </Stack>
              }
            />
            {skip ? null : (
              <DataGridProStyled
                {...config}
                sx={{ minHeight: 400 }}
                apiRef={apiRef}
                autoHeight
                disableColumnMenu
                loading={loading}
                rows={rows}
                getRowId={(row) => row[DimPurchaseOrderItemField.ID]}
                {...gridProps}
                // pagination
                rowCount={rowCount}
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
              />
            )}
          </Grid>
        </Grid>
      </WidgetContent>
    </Widget>
  );
};
